import {
	VelocityEnv,
	OracleInfo,
	Wallet,
	initialize,
} from '@velocity-exchange/sdk';
import { CandleType, TradeOffsetPrice, UIEnv } from '@velocity-exchange/common';
import { Adapter } from '@solana/wallet-adapter-base';
import { PriorityFee, SlippageTolerance } from '../@types/types';
import { Commitment, Version } from '@solana/web3.js';

export enum DriftTheme {
	light = 'light-theme',
	dark = 'dark-theme',
}

export type ToastsToIgnoreSettings = {
	walletVersionedTxCompatibility: boolean;
};

export type SubscriberType = 'polling' | 'websocket';
export type EventSubscriberType = SubscriberType | 'events-server';
export type SelfEventSubscriberType = EventSubscriberType;
export type GlobalEventSubscriberType =
	| EventSubscriberType
	| 'dlob-server-trade-publisher'
	| 'data-api-trade-publisher';
export type CandleTypeByMarketIdKey = Record<string, CandleType>;

export type UserSettings = {
	rpcEndpoint: {
		label: string;
		value: string;
	};
	devnetOverride: boolean;
	stagingHistoryServerOverride: boolean;
	stagingDlobServerOverride: boolean;
	stagingDataServerOverride: boolean;
	enableVersionedTx: boolean;
	showPnlOnClose: boolean;
	showTradeConfirmation: boolean;
	showClosePositionConfirmation: boolean;
	slippageTolerance: SlippageTolerance;
	showAccountValues: boolean;
	showFillNotifications: boolean;
	showZeroValues: boolean;
	blinkOnValueChange: boolean;
	showPositionsAndOrdersOnChart: boolean;
	showLiquidationPriceOnChart: boolean;
	showMarketBar: boolean;
	showElfaWidget: boolean;
	showBuysAndSellsOnChart: boolean;
	perpPageTableSizePreference: string;
	priorityFee: 'custom' | 'dynamic' | 'boosted' | 'turbo';
	customPriorityFee: number | undefined;
	maxPriorityFeeCap: number;
	candleTypeByMarketIdKey: CandleTypeByMarketIdKey;
	enableSwapLeverage: boolean;
	charts?: any[];
	explorer: 'solana' | 'solscan' | 'xray' | 'solanafm';
	auctionDuration: number;
	auctionStartPriceOffsetFrom: TradeOffsetPrice | 'marketBased';
	auctionStartPriceOffset: number | 'marketBased';
	auctionEndPriceOffsetFrom: TradeOffsetPrice;
	auctionEndPriceOffset: number;
	oracleOffsetOrdersEnabled: boolean;
	customMarginRatio: number;
	accountSubscriberType: SubscriberType; // This controls the type of account subscriber used by the Drift Client / RPC. See NetworkSettings.tsx for more details of the different types.
	cancelExistingOrdersOnClosePosition: boolean;
	ignoreToasts: ToastsToIgnoreSettings;
	placeAndTakeEnabled: boolean;
	directRouteSwaps: boolean;
	version?: number;
	traderProfile?: 'default' | 'custom';
	theme: 'dark' | 'light' | 'device' | null;
	globalEventsSubscriptionType: GlobalEventSubscriberType;
	selfEventsSubscriptionType: EventSubscriberType;
	/** This will be `undefined` until the user explicitly disables / enables Swift. */
	swiftEnabled: boolean | undefined;
	showOrderbook: boolean;
	showOrderbookOnMobileTradeView: boolean;
	swapClientType: 'titan' | 'jupiter';
	enableSignlessTrading: boolean;
};

export type EnvironmentVariables = {
	sdkEnv: VelocityEnv;
	sdkConfig: ReturnType<typeof initialize>;
	defaultRpcAddress: string;
	defaultRpcLabel: string;
	historyServerUrl: string;
	dataApiUrl: string;
	dataApiEnvToUse: UIEnv;
	dlobServerHttpUrl: string;
	dlobWsServerToUse: string;
	eventsServerToUse?: string;
	mainnetTermsUrl: string;
	mainnetDisclaimerUrl: string;
	pollingFrequencyMs: number;
	selectedMarketPollingCadenceMs?: number;
	activeMarketPollingCadenceMs?: number;
	backgroundMarketPollingCadenceMs?: number;
	oracleInfos: OracleInfo[];
	maxAccountsPerWallet: number;
	locale: string;
	magicAuthApiKey: string;
	defaultWallet: Wallet;
	E2E_WALLET: Adapter;
	priceDivergenceWarningThreshold: number;
	minInitialDepositValue: number;
	defaultMakerRebateBps: number;
	defaultTakerFeeBps: number;
	dynamicPriorityFeeMultiplier?: number;
	uiEssentialUpdateIntervalMs?: number;
	uiNonEssentialUpdateIntervalMs?: number;
	dynamicFeeTargetPercentile?: number;
	feeSubscriptionPollingMultiplier?: number;
	maxAccountsSwapBuffer: number;
	driftDialectDappAddress: string;
	dialectNotificationsEnabled: boolean;
	useSimulatedComputeUnitBudget: boolean;
	useSimulatedComputeUnitBudgetForFees: boolean;
	computeUnitBufferMultiplier: number;
	minimumPriorityFee: PriorityFee;
	txSenderRetryInterval?: number;
	initialDataSourceMaxDelay: number;
	newDataSourceSubMaxDelay: number;
	upgradeDataSourceDelay: number;
	placeAndTakeLowerBoundCu?: number;
	useCachedBlockhashFetcherInDriftClient: boolean;
	usdsBonusPerWeek: number;
	showAppKitWallet: boolean;
	rpcVersion?: Version;
	defaultAuctionDurationPercentage: number;
	perpsMaxLeverage: number;
	swiftServerUrl: string;
	disablePervasiveOracleCrank: boolean;
	signedMessageOrderSlotBuffer: number;
	marketBasedAuctionStartPriceOffsetMajor: number;
	marketBasedAuctionStartPriceOffsetNonMajor: number;
	enableLstPool: boolean;
	oracleCrankStalenessThreshold: number;
	delistingConfig: { marketIndices: number[]; timestamp: number }[];
	nextDelistingTs: number;
	/**
	 * Number of slots past auction end before we consider it expired (fallback guard).
	 */
	auctionExpirySlotBuffer: number;
	/**
	 * Additional milliseconds added to order.maxTs when checking expiration.
	 */
	orderMaxTsBufferMs: number;
	defaultCommitmentLevel: Commitment;
	/**
	 * API key for Fun.xyz UDA (Universal Deposit Address) integration.
	 */
	funXyzApiKey?: string;
};
